import React from 'react';
import styled from 'styled-components';

import FlexCentered from '../Common/FlexCentered';
import Typography from '../../kingpin/atoms/Typography';

const TINY = 8;
const BUTTON = 16;
const SMALL = 32;
const NORMAL = 48;

const getSize = (size: 'tiny' | 'button' | 'small' | 'normal') => {
  switch (size) {
    case 'button':
      return BUTTON;
    case 'small':
      return SMALL;
    case 'tiny':
      return TINY;
    case 'normal':
    default:
      return NORMAL;
  }
};

const getBorderSize = (size: 'tiny' | 'button' | 'small' | 'normal') => {
  switch (size) {
    case 'tiny':
      return 2;
    case 'button':
      return 2;
    case 'small':
      return 4;
    case 'normal':
    default:
      return 5;
  }
};

const Animation = styled.div<{
  size: 'tiny' | 'button' | 'small' | 'normal';
  color: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;

  /*
   * A single, gently "breathing" ring. Previously this rendered two
   * overlapping rings (pulse + scaleUp ripple) running at 1s — visually busy,
   * especially when many spinners are on screen at once. One calm ring at a
   * slower cadence reads as "loading" without competing for attention.
   */
  .loader {
    width: ${(props) => getSize(props.size)}px;
    height: ${(props) => getSize(props.size)}px;
    border: ${(props) => getBorderSize(props.size)}px solid
      ${(props) => props.color};
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: gentlePulse 1.4s ease-in-out infinite;
  }

  @keyframes gentlePulse {
    0%,
    100% {
      transform: scale(0.92);
      opacity: 0.65;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* Respect users who prefer reduced motion — hold a static ring. */
  @media (prefers-reduced-motion: reduce) {
    .loader {
      animation: none;
      opacity: 0.85;
    }
  }
`;

// eslint-disable-next-line
const Loading = React.memo(
  ({
    isSmall,
    isTiny,
    isButton,
    isGrouped,
    onLoadingAnimationFinished,
    isFinishedLoading,
  }: {
    isTiny?: boolean;
    isButton?: boolean;
    isSmall?: boolean;
    isGrouped?: boolean;
    onLoadingAnimationFinished?: () => void;
    isFinishedLoading?: boolean;
  }) => {
    const color = isGrouped
      ? 'var(--color-spinner-grouped)'
      : 'var(--color-spinner-default)';
    const size = isTiny
      ? 'tiny'
      : isButton
        ? 'button'
        : isSmall
          ? 'small'
          : 'normal';
    return (
      <FlexCentered style={{ padding: 4 }} data-testid="loading">
        <Animation size={size} color={color}>
          <span className="loader"></span>
        </Animation>
        {size === 'normal' && isGrouped && (
          <div style={{ marginTop: 8 }}>
            <Typography.Header type="H6">Loading</Typography.Header>
          </div>
        )}
      </FlexCentered>
    );
  },
);

export default Loading;
